import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from '@app/shared/auth/auth.service';
import { Router, ActivatedRoute, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { PrintService } from './shared/services/helpers/print.service';
import { LoadingService } from './shared/services/helpers/loading.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AnalyticsService } from '@app/shared/services/helpers/analytics.service';
import { IntercomService } from '@app/shared/services/helpers/intercom.service';
import { Subscription } from 'rxjs';
import { ThemeService } from '@app/shared/services/helpers/theme.service';
import { closeOpenRowActionDropdownMenus } from '@app/shared/utils/close-open-row-action-dropdown-menus';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  isLoading = this.loadingService.isLoading$;
  private subscriptions = new Subscription();
  private loginProfile: any = null;
  private modalOpenObserver: MutationObserver | null = null;

  constructor(
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    public printSvc: PrintService,
    private loadingService: LoadingService,
    private spinner: NgxSpinnerService,
    private analyticsService: AnalyticsService,
    private intercomService: IntercomService,
    private themeService: ThemeService
  ) {
    this.themeService.initialize();
    this.syncThemeUserScope();
    // Handle SSO token navigation after initialization
    this.handleNavigationAfterSSOToken();
  }

  ngOnInit() {
    this.analyticsService.init();
    this.subscriptions.add(this.auth.loginProfile$.subscribe((profile) => {
      this.loginProfile = profile;
      this.intercomService.boot(this.auth.user);
    }));

    this.subscriptions.add(this.auth.authenticationState.subscribe((isAuthenticated) => {
      if (!isAuthenticated) {
        this.themeService.setUserScope(null);
        this.intercomService.shutdown();
        return;
      }

      this.syncThemeUserScope();
      this.auth.getLoginProfile();
      this.intercomService.boot(this.auth.user);
    }));

    this.subscriptions.add(this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loadingService.show();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.loadingService.hide();
      }

      if (event instanceof NavigationEnd) {
        this.intercomService.update();
      }
    }));

    this.watchModalOpenState();
  }

  ngOnDestroy() {
    this.modalOpenObserver?.disconnect();
    this.subscriptions.unsubscribe();
  }

  /** Row action menus use container="body"; close them whenever any modal opens. */
  private watchModalOpenState(): void {
    this.modalOpenObserver = new MutationObserver(() => {
      if (document.body.classList.contains('modal-open')) {
        closeOpenRowActionDropdownMenus();
      }
    });

    this.modalOpenObserver.observe(document.body, {
      attributeFilter: ['class'],
      attributes: true,
    });
  }

  async initializeApp() {

  }

  /**
   * Handle navigation after SSO token exchange
   */
  public async handleNavigationAfterSSOToken() {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      // Show loading spinner while processing SSO token
      this.spinner.show();

      try {
        // Exchange SSO token for authentication
        const success = await this.auth.handleSSOTokenExchange(token);

        if (success) {
          // Get return path before cleanup
          const returnTo = urlParams.get('return_to') || '/landing';
          // Remove SSO params from URL
          this.removeSSOParamsFromUrl();
          // Navigate to target path
          this.router.navigate([returnTo]);
        } else {
          // SSO token exchange failed, redirect to login
          this.auth.authenticationState.next(false);
          this.router.navigate(['/auth/login']);
        }
      } catch (error) {
        console.error('SSO token exchange failed:', error);
        this.auth.authenticationState.next(false);
        this.router.navigate(['/auth/login']);
      } finally {
        // Hide loading spinner
        this.spinner.hide();
      }
    }
  }

  /**
   * Remove SSO parameters from URL without page reload
   */
  private removeSSOParamsFromUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    url.searchParams.delete('state');
    url.searchParams.delete('return_to');
    window.history.replaceState({}, document.title, url.pathname + url.search);
  }

  logout() {
    this.auth.logout();
  }

  private syncThemeUserScope(): void {
    const accountId = this.auth.getUserData('account_id');
    const userId = this.auth.getUserData('id');
    const loginType = this.auth.getUserData('login_type');

    if (!accountId || !userId || !loginType) {
      return;
    }

    this.themeService.setUserScope({ accountId, userId, loginType });
  }
}
